import type { ReactNode } from 'react';
import { useCreatePath, useRecordContext, type RaRecord } from 'react-admin';
import { Link } from 'react-router-dom';
import { Alert, AlertTitle, Box, Card, CardContent, Chip, Stack, Tooltip, Typography } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { plural } from '../../../shared/plural';
import { dateTimeNoSeconds } from '../../../shared/dateTime';
import { windowText } from '../bankRowText';
import { statusSinceText } from '../statusSince';
import { isExpired, planningStatusAppearance, STATUS_PRE_ORDER } from '../planningStatus';
import { acceptanceStatusAppearance } from '../acceptanceStatus';
import { useStatuses } from '../statusDictionary';
import { tmsOrderStatusText } from '../tmsOrderStatus';
import { ExchangeCell } from '../cells';
import { lostVehicleAtOf } from '../markers';
import { missingFieldLabel } from '../preOrderFields';
import { OriginTag } from './OriginTag';
import { assignedCarOrigin, planningStatusOrigin, recordOrigin } from './fieldOrigin';
import { orderStages, STAGE_STATE_LABELS, type OrderStage } from './orderStages';
import { ExecutionHintChip, LeadDepartmentChip } from '../processLead';
import { TradeTransferCaption } from '../tradeTransferInfo';

/**
 * Nagłówek okna zlecenia (makieta FTL p. Mariusza, pkt 12; Etap 1, decyzja
 * Bartka 15.10.2026): numer, relacja z liczbą punktów trasy, żetony stanu,
 * pasek etapów i pasek podsumowania. Pod nim — baner prezlecenia.
 *
 * Czego tu nie ma, choć makieta to pokazuje: **punktu odcięcia** (czeka na
 * decyzję p. Mariusza nr 1 — ile godzin i kto zatwierdza po nim) i
 * **szacowanego przyjazdu** w pasku (liczy go przeliczenie podpowiedzi —
 * jest przy obsadzonym aucie w „Wariantach obsadzenia”, nie liczymy go
 * drugi raz dla paska).
 */

type Loose = Record<string, unknown>;

const text = (v: unknown): string | null => (typeof v === 'string' && v.trim() !== '' ? v.trim() : null);

/** „PL 62-240 Trzemeszno” — kraj, kod, miasto; czego nie ma, to pomijamy. */
export const placeText = (country: unknown, postalCode: unknown, city: unknown): string =>
  [country, postalCode, city].map(text).filter(Boolean).join(' ') || '—';

/** Relacja zlecenia: pierwszy załadunek → ostatni rozładunek. */
export const relationText = (r: Loose): string =>
  `${placeText(r.fromCountry, r.fromPostalCode, r.fromCity)} → ${placeText(r.toCountry, r.toPostalCode, r.toCity)}`;

/** „4 punkty trasy” — z punktów TMS; bez punktów nic (prezlecenie ich nie ma). */
export const pointsCountText = (r: Loose): string | null => {
  const points = Array.isArray(r.keyPoints) ? r.keyPoints.length : 0;
  return points > 0 ? plural(points, 'punkt trasy', 'punkty trasy', 'punktów trasy') : null;
};

const STAGE_COLORS: Record<OrderStage['state'], { border: string; bg: string; color: string }> = {
  done: { border: 'success.main', bg: 'transparent', color: 'text.primary' },
  current: { border: 'primary.main', bg: 'action.selected', color: 'text.primary' },
  todo: { border: 'divider', bg: 'transparent', color: 'text.secondary' },
  noData: { border: 'divider', bg: 'transparent', color: 'text.disabled' },
};

/** Pasek etapów — stan każdego etapu słowem pod nazwą, powód w dymku. */
export const StageBar = ({ stages }: { stages: OrderStage[] }) => (
  <Box
    component="ol"
    aria-label="Etapy zlecenia"
    sx={{
      listStyle: 'none',
      p: 0,
      m: 0,
      display: 'grid',
      gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(4, minmax(0, 1fr))', lg: 'repeat(7, minmax(0, 1fr))' },
      gap: 0.5,
    }}
  >
    {stages.map((s) => {
      const c = STAGE_COLORS[s.state];
      return (
        <Tooltip key={s.key} title={s.detail}>
          <Box
            component="li"
            data-testid={`stage-${s.key}`}
            data-state={s.state}
            aria-label={`${s.label}: ${STAGE_STATE_LABELS[s.state]}. ${s.detail}`}
            tabIndex={0}
            sx={{
              borderTop: 3,
              borderColor: c.border,
              bgcolor: c.bg,
              px: 1,
              py: 0.5,
              minWidth: 0,
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: s.state === 'current' ? 600 : 400, color: c.color }} noWrap>
              {s.label}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap component="p">
              {STAGE_STATE_LABELS[s.state]}
            </Typography>
          </Box>
        </Tooltip>
      );
    })}
  </Box>
);

/** Chip, którego treść się zawija zamiast wychodzić poza wąską kolumnę. */
const WRAP_CHIP_SX = { maxWidth: '100%', height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', py: 0.25 } };

/** Jedna komórka paska podsumowania: etykieta, wartość, dopisek. */
const SummaryItem = ({ label, children, caption }: { label: string; children: ReactNode; caption?: ReactNode }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography variant="caption" color="text.secondary" component="p">
      {label}
    </Typography>
    <Box sx={{ typography: 'body2' }}>{children}</Box>
    {caption ? (
      <Typography variant="caption" color="text.secondary" component="p">
        {caption}
      </Typography>
    ) : null}
  </Box>
);

/** Obsadzone auto jako odsyłacz do karty pojazdu. */
const CarLink = ({ id, label }: { id: number; label: string }) => {
  const createPath = useCreatePath();
  return <Link to={createPath({ resource: 'cars', type: 'show', id })}>{label}</Link>;
};

/**
 * Pasek podsumowania: stan planowania, terminy, pojazd, status w TMS
 * i giełdy — **trzy osie osobno** (stan planowania, informacja z TMS,
 * publikacja per giełda), jak w makiecie i jak w banku.
 */
export const SummaryBar = ({ record }: { record: RaRecord }) => {
  const states = useStatuses();
  const appearance = planningStatusAppearance(record.planningStatus, states);
  const car = record.assignedCar as Loose | undefined;
  const rawCarId = car?.id ?? record.assignedCarId;
  const carId = rawCarId === null || rawCarId === undefined || rawCarId === '' ? null : Number(rawCarId);
  const hasCar = carId !== null && Number.isInteger(carId);
  const plate = hasCar ? (text(car?.registrationNumber) ?? `#${carId}`) : null;
  const carrier = text((record.carrier as Loose | undefined)?.name);
  const variants = Number((record._count as Loose | undefined)?.candidates ?? NaN);
  const hasExchanges = Array.isArray(record.exchanges);
  return (
    <Box
      data-testid="order-summary"
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))', lg: 'repeat(6, minmax(0, 1fr))' },
        gap: 2,
      }}
    >
      <SummaryItem
        label="Stan planowania"
        caption={statusSinceText(record.planningStatusSince, record.planningStatusBy as { username?: string | null } | null)}
      >
        <Stack direction="row" spacing={0.5} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Długi stan („Obsadzone przewoźnikiem”) zawija się w wąskiej kolumnie przy 1366 px,
              zamiast ucinać się wielokropkiem bez dymka (audyt układu 23.10.2026). */}
          <Chip size="small" color={appearance.color} label={appearance.label} sx={WRAP_CHIP_SX} />
          <OriginTag origin={planningStatusOrigin(record)} />
        </Stack>
        {Number.isFinite(variants) && variants > 0 ? (
          <Typography variant="caption" component="p">
            {plural(variants, 'aktywny wariant', 'aktywne warianty', 'aktywnych wariantów')}
          </Typography>
        ) : null}
        {/* Powód przekazania do Trade / zwrotu z listy (A6, 20.10.2026). */}
        <TradeTransferCaption record={record} />
      </SummaryItem>
      <SummaryItem label="Załadunek" caption={placeText(record.fromCountry, record.fromPostalCode, record.fromCity)}>
        {windowText(
          (record.loadingDateFrom ?? record.fromDate) as string | null,
          (record.loadingDateTo as string | null | undefined) ?? null,
        )}
      </SummaryItem>
      <SummaryItem label="Rozładunek" caption={placeText(record.toCountry, record.toPostalCode, record.toCity)}>
        {windowText(
          (record.unloadingDateFrom as string | null | undefined) ?? null,
          (record.unloadingDateTo ?? record.toDate) as string | null,
        )}
      </SummaryItem>
      <SummaryItem
        label="Pojazd"
        caption={
          plate
            ? text(car?.ftlCaretaker)
              ? `opiekun: ${text(car?.ftlCaretaker)}`
              : 'opiekun: —'
            : carrier
              ? 'przewoźnik z TMS'
              : 'zlecenie nie ma pojazdu'
        }
      >
        <Stack direction="row" spacing={0.5} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
          {plate && carId !== null ? (
            <CarLink id={carId} label={plate} />
          ) : (
            <span>{carrier ?? 'brak'}</span>
          )}
          <OriginTag origin={plate ? assignedCarOrigin(record) : carrier ? recordOrigin(record) : null} />
        </Stack>
      </SummaryItem>
      <SummaryItem label="Status w TMS" caption="informacja z TMS">
        {tmsOrderStatusText(record.tmsOrderStatus)}
      </SummaryItem>
      <SummaryItem label="Giełdy" caption={hasExchanges ? 'osobno każda giełda' : 'brak danych o publikacjach'}>
        {hasExchanges ? <ExchangeCell record={record} /> : '—'}
      </SummaryItem>
    </Box>
  );
};

/**
 * Baner prezlecenia (makieta pkt 12: „prezlecenie widoczne z działem,
 * w którym się znajduje, i listą braków; można tworzyć warianty, nie można
 * obsadzić”). U nas obsadzenie wymaga akceptacji prezlecenia — mówi to też
 * karta akcji (FTL §15).
 */
export const PreOrderBanner = ({ record }: { record: RaRecord }) => {
  if (record.planningStatus !== STATUS_PRE_ORDER) return null;
  const p = record.preOrder as Loose | undefined;
  const dept = text(p?.responsibleDept);
  const missing = Array.isArray(p?.missingFields) ? (p?.missingFields as string[]) : [];
  return (
    <Alert severity="info" data-testid="pre-order-banner">
      <AlertTitle>Prezlecenie — jeszcze nie w TMS</AlertTitle>
      Dział: {dept ?? 'nie wskazano'}.{' '}
      {missing.length > 0 ? `Braki: ${missing.map(missingFieldLabel).join(', ')}.` : 'Bez braków do akceptacji.'}
      <Typography variant="body2" component="p" sx={{ mt: 0.5 }}>
        Możesz dodawać warianty. Obsadzić pojazdem można po akceptacji prezlecenia.
      </Typography>
    </Alert>
  );
};

/** Nagłówek z numerem, relacją, żetonami, paskiem etapów i podsumowaniem. */
export const OrderWindowHeader = () => {
  const r = useRecordContext();
  const states = useStatuses();
  if (!r) return null;
  const tms = acceptanceStatusAppearance(r.acceptanceStatus);
  const points = pointsCountText(r);
  const lostAt = lostVehicleAtOf(r);
  const number = text(r.externalId) ?? `#${String(r.id)}`;
  return (
    <Card variant="outlined" data-testid="order-window-header">
      <CardContent sx={{ '&:last-child': { pb: 2 } }}>
        <Stack spacing={2}>
          {/* Kontrakt `data-testid` paczki (S-22.10-02): nagłówek zlecenia — FTL na całym bloku, Trade na tytule z numerem. */}
          <Box data-testid="ftl-zlecenie-naglowek">
            <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: 'baseline', flexWrap: 'wrap' }}>
              <Typography variant="h5" component="h1" data-testid="trd-zlecenie-naglowek">
                Zlecenie {number}
              </Typography>
              <OriginTag origin={recordOrigin(r)} />
              <Typography variant="subtitle1" color="text.secondary" component="span">
                {relationText(r)}
                {points ? ` · ${points}` : ''}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap', mt: 1 }}>
              <Chip
                size="small"
                color={planningStatusAppearance(r.planningStatus, states).color}
                label={planningStatusAppearance(r.planningStatus, states).label}
              />
              <Tooltip title="Stan zapisu zlecenia do TMS">
                <Chip size="small" label={tms.label} sx={{ color: tms.textColor, backgroundColor: tms.backgroundColor }} />
              </Tooltip>
              {/* „Prowadzi: …” i „wg TMS: …” (odpowiedzi 16.10.2026, pyt. 55,
                  4a i 30) — tylko gdy API niesie pola (przełączniki). */}
              <LeadDepartmentChip record={r} />
              <ExecutionHintChip record={r} />
              {r.atRisk ? (
                <Chip
                  size="small"
                  color="error"
                  icon={<WarningAmberIcon />}
                  label={(r.riskReason as string) || 'Zgłoszone ryzyko'}
                />
              ) : null}
              {isExpired(r.validUntil as string) ? (
                <Chip size="small" color="error" variant="outlined" label="Po terminie ważności" />
              ) : null}
              {lostAt ? (
                <Chip
                  size="small"
                  color="error"
                  variant="outlined"
                  label={`Straciło pojazd ${dateTimeNoSeconds(lostAt)}`}
                />
              ) : null}
            </Stack>
          </Box>
          <StageBar stages={orderStages(r, states)} />
          <SummaryBar record={r} />
        </Stack>
      </CardContent>
    </Card>
  );
};
